import React, {
	type ComponentType,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	// eslint-disable-next-line jira/restricted/react
	PureComponent,
	type ReactElement,
} from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from '@atlassian/jira-software-board-css-module/src/column-header.module.css';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { useBoardHasScrollTop } from '../../../controllers/board-scroll/index.tsx';
import { zIndex, swimlaneHeaderHeight } from '../../constants/styles/index.tsx';
import {
	type ValidateColumnCallback,
	type ColumnId,
	type ColumnLozengeValues,
	ColumnTheme,
} from '../../types.tsx';
import { isStickySupported } from '../../utils/is-sticky-supported/index.tsx';
import { Overlay } from '../overlay/index.tsx';
import ColumnTitleLozenges from './editable-title/column-title/lozanges/index.tsx';
import ColumnEditableTitle from './editable-title/main.tsx';
import ColumnHeaderV2 from './header/index.tsx';

export type Lozenges = (string | ColumnLozengeValues)[];

type ColumnHeaderV2Props = ComponentProps<typeof ColumnHeaderV2>;
export type ColumnMenu = ColumnHeaderV2Props['menu'];

export type Props = {
	isShadowVisible?: boolean;
	isRenamable?: boolean;
	isDraggable: boolean;
	isMenuShown?: boolean | null;
	// injected by column
	isUpdating?: boolean;
	isDisabled?: boolean;
	isDragging?: boolean;
	isDone: boolean;
	isCMPBoard: boolean;
	shouldRemoveSpaceForMenu?: boolean;
	isFlexibleColumns?: boolean | null;
	id: ColumnId;
	title: string;
	lozenges?: Lozenges | null | undefined;
	cardsCount?: number;
	totalCardsCount?: number;
	showTotalCardsCount?: boolean;
	validateRename?: ValidateColumnCallback;
	appearance?: ColumnTheme;
	menu?: ColumnMenu;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	OverlayContent?: ComponentType<any> | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onRename: (id: ColumnId, name: string) => any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onEditStart?: () => any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onEditCancel?: (columnId: ColumnId, type: string) => any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onHeaderClick?: () => any;
	isMatrixLayout?: boolean;
	columnHeaderWrapperRef?: React.MutableRefObject<HTMLDivElement | null>;
};

type State = {
	isEditingTitle: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class ColumnHeader extends PureComponent<Props, State> {
	static defaultProps = {
		isRenamable: true,
		isShadowVisible: false,
		isDragging: false,
		isUpdating: false,
		isDisabled: false,
		isDone: false,
		cardsCount: 0,
		totalCardsCount: 0,
		showTotalCardsCount: false,
		lozenges: null,
		validateRename: () => ({
			isValid: true,
			message: null,
		}),
		appearance: 'default',
		isCMPboard: false,
		isFlexibleColumns: false,
		shouldRemoveSpaceForMenu: false,
	};

	state = {
		isEditingTitle: false,
	};

	onColumnTitleEditChange = (isEditingTitle: boolean) => {
		this.setState({
			isEditingTitle,
		});

		if (isEditingTitle && this.props.onEditStart) {
			this.props.onEditStart();
		}
	};

	onColumnTitleEditCancel = (columnId: ColumnId, type: string) => {
		this.props.onEditCancel && this.props.onEditCancel(columnId, type);
	};

	onHeaderClick = () => {
		this.props.onHeaderClick && this.props.onHeaderClick();
	};

	getColumnLozenges(
		lozenges?: Lozenges | null,
	): ReactElement<ComponentProps<typeof ColumnTitleLozenges>> | null {
		if (!lozenges || lozenges.length === 0) {
			return null;
		}
		return <ColumnTitleLozenges lozenges={lozenges} appearance={this.props.appearance} />;
	}

	render() {
		const {
			id,
			title,
			lozenges,
			isShadowVisible,
			isUpdating,
			isDisabled,
			isDragging,
			isRenamable,
			isDraggable,
			cardsCount,
			totalCardsCount,
			showTotalCardsCount,
			validateRename,
			menu,
			OverlayContent,
			isMenuShown,
			isDone,
			isCMPBoard,
			shouldRemoveSpaceForMenu,
			isFlexibleColumns,
			columnHeaderWrapperRef,
		} = this.props;

		const { isEditingTitle } = this.state;

		const isTitleDisabled = isDisabled || isDragging || !isRenamable;

		return (
			<ColumnHeaderWrapper
				isDragging={isDragging}
				isShadowVisible={isShadowVisible}
				isFlexibleColumns={isFlexibleColumns}
				ref={columnHeaderWrapperRef}
				{...(getIsSoftwareThemingM2Enabled() && {
					isMatrixLayout: this.props.isMatrixLayout,
				})}
			>
				<ColumnHeaderV2
					id={id}
					title={
						<ColumnEditableTitle
							columnId={id}
							text={title}
							visibleCount={cardsCount || 0}
							totalCount={totalCardsCount}
							showTotalCount={showTotalCardsCount}
							isDisabled={isTitleDisabled}
							onEditChange={this.onColumnTitleEditChange}
							onConfirm={this.props.onRename}
							onCancel={this.onColumnTitleEditCancel}
							lozenges={this.getColumnLozenges(lozenges)}
							// @ts-expect-error - Type 'ValidateColumnCallback | undefined' is not assignable to type '(() => { isValid: boolean; message: null; }) | undefined'.
							validateColumn={validateRename}
							isDone={isDone}
							isCMPBoard={isCMPBoard}
							shouldRemoveSpaceForMenu={shouldRemoveSpaceForMenu}
						/>
					}
					titleHeadingText={title}
					isEditingTitle={isEditingTitle}
					isMenuShown={isMenuShown}
					isSpinnerVisible={isUpdating}
					isDragging={isDragging}
					isDraggable={isDraggable}
					menu={menu}
					onClick={this.onHeaderClick}
					OverlayContent={OverlayContent}
					appearance={this.props.appearance}
					isMatrixLayout={this.props.isMatrixLayout}
				/>
				{(isUpdating || isDisabled) && (
					<Overlay appearance={this.props.appearance ?? ColumnTheme.Default} />
				)}
			</ColumnHeaderWrapper>
		);
	}
}

export type OwnProps = {
	isRenamable?: boolean;
	isDraggable: boolean;
	isMenuShown?: boolean | null;
	// injected by column
	isUpdating?: boolean;
	isDisabled?: boolean;
	isDragging?: boolean;
	isDone?: boolean;
	isCMPBoard: boolean;
	shouldRemoveSpaceForMenu?: boolean;
	isFlexibleColumns?: boolean | null;
	id: ColumnId;
	title: string;
	lozenges?: Lozenges | null | undefined;
	cardsCount?: number;
	totalCardsCount?: number;
	showTotalCardsCount?: boolean;
	validateRename?: ValidateColumnCallback;
	appearance?: ColumnTheme;
	menu?: ColumnMenu;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	OverlayContent?: ComponentType<any> | undefined;
	showShadowOnScroll: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onRename: (id: ColumnId, name: string) => any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onEditStart?: () => any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onEditCancel?: (columnId: ColumnId, type: string) => any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onHeaderClick?: () => any;
	isMatrixLayout?: boolean;
	columnHeaderWrapperRef?: React.MutableRefObject<HTMLDivElement | null>;
};

const ColumnHeaderMain = ({ showShadowOnScroll, ...props }: OwnProps) => {
	const [hasScrollTop] = useBoardHasScrollTop();

	return <ColumnHeader {...props} isShadowVisible={showShadowOnScroll && hasScrollTop} />;
};

export default ColumnHeaderMain;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColumnHeaderContainerBackground = (isDragging: any) => {
	if (isDragging) return 'none';
	return token('color.background.neutral');
};

type ColumnHeaderWrapperType = {
	isDragging?: boolean;
	isShadowVisible?: boolean;
	isFlexibleColumns?: boolean | null;
	isMatrixLayout?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnHeaderWrapperBase = styled.div<ColumnHeaderWrapperType>({
	height: 'unset',
	display: 'flex',
	flexFlow: 'column nowrap',
	justifyContent: 'flex-start',
	alignItems: 'stretch',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	zIndex: (props: ColumnHeaderWrapperType) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		props.isMatrixLayout && getIsSoftwareThemingM2Enabled() ? 1 : zIndex.stickyHeaders,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	container: (props: ColumnHeaderWrapperType) =>
		props.isFlexibleColumns
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`${styles['column-header']} / inline-size`
			: '',
});

const ColumnHeaderWrapper = isStickySupported
	? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
		styled(ColumnHeaderWrapperBase)({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			position: (props: ColumnHeaderWrapperType) => (props.isDragging ? 'relative' : 'sticky'),
			/*
            When column has scroll top < 0, column content is displayed underneath header,
            and as column content has grey background, rounded corners are gone.
            background-color: white is used to overlay column content => keep header corners rounded.
          */
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			background: (props: ColumnHeaderWrapperType) =>
				getColumnHeaderContainerBackground(props.isDragging),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			top: (props: ColumnHeaderWrapperType) =>
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- For feature gate
				props.isMatrixLayout && getIsSoftwareThemingM2Enabled() ? `${swimlaneHeaderHeight}px` : 0,
			borderRadius: '6px',
			'&::before': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				opacity: (props: ColumnHeaderWrapperType) => (props.isShadowVisible ? 0.2 : 0),
				transition: 'opacity 0.2s',
				content: '',
				position: 'absolute',
				bottom: token('space.negative.025'),
				left: 0,
				right: 0,
				height: '2px',
				pointerEvents: 'none',

				backgroundColor: token('color.blanket'),
			},
		})
	: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
		styled(ColumnHeaderWrapperBase)({
			position: 'relative',
			borderRadius: '6px',
		});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export { ColumnHeaderWrapper };
